import { type FormEvent, useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LogIn, ShieldAlert, Fingerprint, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { IdentityApiError, useLogin, useLoginTwoFactor, useLoginWithPasskey, useSamlProviders } from "@/api/hooks";
import { identityApi, identityServiceUrl, type SessionResponse, type TwoFactorMethod } from "@/api/identityApi";
import { resolveSafeReturnUrl } from "@/lib/returnUrl";
import { passkeysSupported } from "@/lib/webauthn";
import { PhoneApproval } from "@/features/auth/PhoneApproval";

type Stage = "password" | "twofactor" | "push" | "passwordless-email" | "passwordless-wait";

export function LoginPage() {
    const [params] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [stage, setStage] = useState<Stage>("password");
    const [methods, setMethods] = useState<TwoFactorMethod[]>([]);
    const [code, setCode] = useState("");
    const [useRecovery, setUseRecovery] = useState(false);
    const login = useLogin();
    const twoFactor = useLoginTwoFactor();
    const passkeyLogin = useLoginWithPasskey();
    const samlProviders = useSamlProviders();
    const navigate = useNavigate();
    const qc = useQueryClient();

    const rawReturnUrl = params.get("returnUrl");
    const safeReturnUrl = resolveSafeReturnUrl(rawReturnUrl);

    const redirectAfterLogin = () => {
        if (rawReturnUrl) window.location.assign(resolveSafeReturnUrl(rawReturnUrl));
        else navigate("/", { replace: true });
    };

    // A phone approval completes the sign-in server-side; cache the session and continue.
    const finishWithSession = (session: SessionResponse) => {
        qc.setQueryData(["session"], session);
        redirectAfterLogin();
    };

    // The returnUrl (e.g. /connect/authorize?client_id=…) lets the phone show which app is signing in.
    const startPush = useCallback(() => identityApi.pushStart(rawReturnUrl ?? undefined), [rawReturnUrl]);
    const startPasswordless = useCallback(
        () => identityApi.passwordlessStart(email.trim(), rawReturnUrl ?? undefined),
        [email, rawReturnUrl]
    );

    // Email-first SSO discovery: once the email looks complete, check whether its domain has
    // any enabled SAML providers — if so, show them alongside the password field. A lookup
    // failure (network hiccup, unknown domain) just means no buttons, never blocks the
    // ordinary password flow below.
    const handleEmailBlur = () => {
        if (email.includes("@") && !samlProviders.data && !samlProviders.isPending) {
            samlProviders.mutate(email);
        }
    };

    const handleSamlLogin = (idpId: string) => {
        window.location.assign(
            `${identityServiceUrl}/saml/${idpId}/login?returnUrl=${encodeURIComponent(safeReturnUrl)}`
        );
    };

    const toMessage = (err: unknown): string => {
        if (err instanceof IdentityApiError) {
            return err.detail ? `${err.message} — ${err.detail}` : err.message;
        }
        if (err instanceof TypeError) {
            return (
                "Couldn't reach the identity service. Make sure it's running at " +
                (import.meta.env.VITE_IDENTITY_SERVICE_URL ?? "") +
                " and that your browser accepts the cross-origin request."
            );
        }
        return "Sign-in failed.";
    };

    const resetToPassword = () => {
        setStage("password");
        setCode("");
        setPassword("");
        setError(null);
        setUseRecovery(false);
        setMethods([]);
    };

    // From the phone screen to typing a code. A push-only account has no authenticator-app code,
    // so it starts on recovery codes.
    const useCodeInstead = () => {
        setError(null);
        setCode("");
        setUseRecovery(methods.length > 0 && !methods.includes("totp"));
        setStage("twofactor");
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        try {
            const resp = await login.mutateAsync({ email, password });
            if (resp.requiresTwoFactor) {
                const available = resp.twoFactorMethods ?? [];
                setMethods(available);
                if (available.includes("push")) {
                    setStage("push");
                } else {
                    setUseRecovery(available.length > 0 && !available.includes("totp"));
                    setStage("twofactor");
                }
                return;
            }
            if (resp.mustUsePasskey) {
                // Domain requires MFA and this user has a passkey — password alone
                // isn't accepted. Nudge them to the passkey button.
                setError("Your organisation requires two-step sign-in. Use “Sign in with a passkey” below.");
                return;
            }
            if (resp.mfaSetupRequired) {
                // Signed in on a grace pass, but must set up MFA before continuing.
                navigate("/security", { replace: true });
                return;
            }
            redirectAfterLogin();
        } catch (err) {
            setError(toMessage(err));
        }
    };

    const handlePasskey = async () => {
        setError(null);
        try {
            const resp = await passkeyLogin.mutateAsync();
            if (resp.session) redirectAfterLogin();
            else setError("Passkey sign-in didn't complete. Try again.");
        } catch (err) {
            // User dismissed the OS prompt — stay quiet, not an error.
            if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError")) return;
            setError(
                err instanceof IdentityApiError
                    ? "No matching passkey for this site, or it was declined."
                    : toMessage(err)
            );
        }
    };

    const handleTwoFactor = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        try {
            const resp = await twoFactor.mutateAsync({ code, isRecoveryCode: useRecovery });
            if (resp.session) redirectAfterLogin();
            else setError("That code wasn't accepted. Try again.");
        } catch (err) {
            setError(
                err instanceof IdentityApiError
                    ? (useRecovery ? "That recovery code isn't valid." : "That code isn't valid or has expired.")
                    : toMessage(err)
            );
        }
    };

    const describePushStartError = (err: unknown): string | null => {
        if (!(err instanceof IdentityApiError)) return null;
        if (err.status === 401) return "Your sign-in timed out. Start over and enter your password again.";
        if (err.status === 409) return "No phone is set up to approve sign-ins. Use a verification code instead.";
        return null;
    };

    if (stage === "push") {
        return (
            <PhoneApproval
                title="Approve sign-in on your phone"
                description="We sent a request to Athavita Authenticator. Open it and tap the number below to finish signing in."
                start={startPush}
                poll={identityApi.pushStatus}
                onApproved={finishWithSession}
                describeStartError={describePushStartError}
            >
                <button type="button" className="text-muted-foreground hover:underline" onClick={useCodeInstead}>
                    Use a verification code instead
                </button>
                <button type="button" className="text-muted-foreground hover:underline" onClick={resetToPassword}>
                    Start over
                </button>
            </PhoneApproval>
        );
    }

    if (stage === "passwordless-wait") {
        return (
            <PhoneApproval
                title="Check your phone"
                description={
                    <>
                        If <span className="font-medium text-foreground">{email.trim()}</span> uses Athavita
                        Authenticator with passwordless sign-in turned on, a request is on its way. Tap the number
                        below to sign in.
                    </>
                }
                start={startPasswordless}
                poll={identityApi.passwordlessStatus}
                onApproved={finishWithSession}
                expiredHint="No request on your phone? Turn on passwordless sign-in in the app's settings, or use your password."
            >
                <button type="button" className="text-muted-foreground hover:underline" onClick={() => setStage("passwordless-email")}>
                    Use a different email
                </button>
                <button type="button" className="text-muted-foreground hover:underline" onClick={resetToPassword}>
                    Use my password instead
                </button>
            </PhoneApproval>
        );
    }

    if (stage === "passwordless-email") {
        const valid = email.trim().includes("@");
        return (
            <Card className="w-full">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Smartphone className="h-5 w-5 text-primary" aria-hidden /> Sign in without a password
                    </CardTitle>
                    <CardDescription>
                        Approve the sign-in on your phone with Athavita Authenticator. Turn on passwordless sign-in in
                        the app first.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form
                        className="space-y-4"
                        noValidate
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (valid) setStage("passwordless-wait");
                        }}
                    >
                        <div className="space-y-1.5">
                            <Label htmlFor="pl-email">Email</Label>
                            <Input
                                id="pl-email"
                                type="email"
                                autoComplete="username"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <Button type="submit" className="w-full" disabled={!valid}>
                            <Smartphone className="h-4 w-4" aria-hidden />
                            Send a request to my phone
                        </Button>
                    </form>
                    <div className="mt-4 text-sm">
                        <button type="button" className="text-muted-foreground hover:underline" onClick={resetToPassword}>
                            Use my password instead
                        </button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (stage === "twofactor") {
        const canUseTotp = methods.length === 0 || methods.includes("totp");
        return (
            <Card className="w-full">
                <CardHeader>
                    <CardTitle>Two-step verification</CardTitle>
                    <CardDescription>
                        {useRecovery
                            ? "Enter one of your saved recovery codes."
                            : "Enter the 6-digit code from your authenticator app."}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {error ? (
                        <Alert variant="destructive" className="mb-4">
                            <ShieldAlert className="h-4 w-4" />
                            <AlertTitle>Couldn't verify</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : null}
                    <form onSubmit={handleTwoFactor} className="space-y-4" noValidate>
                        <div className="space-y-1.5">
                            <Label htmlFor="code">{useRecovery ? "Recovery code" : "Authenticator code"}</Label>
                            <Input
                                id="code"
                                inputMode={useRecovery ? "text" : "numeric"}
                                autoComplete="one-time-code"
                                required
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                placeholder={useRecovery ? "xxxxx-xxxxx" : "123456"}
                                autoFocus
                            />
                        </div>
                        <Button type="submit" className="w-full" disabled={twoFactor.isPending || !code.trim()}>
                            <LogIn className="h-4 w-4" />
                            {twoFactor.isPending ? "Verifying…" : "Verify"}
                        </Button>
                    </form>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
                        {methods.includes("push") ? (
                            <button
                                type="button"
                                className="text-muted-foreground hover:underline"
                                onClick={() => { setError(null); setStage("push"); }}
                            >
                                Approve on my phone instead
                            </button>
                        ) : null}
                        {canUseTotp ? (
                            <button
                                type="button"
                                className="text-muted-foreground hover:underline"
                                onClick={() => { setUseRecovery((v) => !v); setCode(""); setError(null); }}
                            >
                                {useRecovery ? "Use authenticator app instead" : "Use a recovery code"}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="text-muted-foreground hover:underline"
                            onClick={resetToPassword}
                        >
                            Start over
                        </button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle>Sign in</CardTitle>
                <CardDescription>to continue to the Athavita Enterprise Suite</CardDescription>
            </CardHeader>
            <CardContent>
                {error ? (
                    <Alert variant="destructive" className="mb-4">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Couldn't sign you in</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div className="space-y-1.5">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                samlProviders.reset();
                            }}
                            onBlur={handleEmailBlur}
                            autoFocus
                        />
                    </div>
                    {samlProviders.data && samlProviders.data.length > 0 ? (
                        <div className="space-y-2">
                            {samlProviders.data.map((p) => (
                                <Button
                                    key={p.id}
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => handleSamlLogin(p.id)}
                                >
                                    {p.buttonLabel || `Continue with ${p.provider}`}
                                </Button>
                            ))}
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="h-px flex-1 bg-border" /> or use a password <span className="h-px flex-1 bg-border" />
                            </div>
                        </div>
                    ) : null}
                    <div className="space-y-1.5">
                        <Label htmlFor="password">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>
                    <Button type="submit" className="w-full" disabled={login.isPending}>
                        <LogIn className="h-4 w-4" />
                        {login.isPending ? "Signing in…" : "Sign in"}
                    </Button>
                </form>
                <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
                </div>
                <div className="space-y-2">
                    {passkeysSupported() ? (
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={handlePasskey}
                            disabled={passkeyLogin.isPending}
                        >
                            <Fingerprint className="h-4 w-4" />
                            {passkeyLogin.isPending ? "Waiting for your device…" : "Sign in with a passkey"}
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => { setError(null); setStage("passwordless-email"); }}
                    >
                        <Smartphone className="h-4 w-4" />
                        Sign in without a password
                    </Button>
                </div>
            </CardContent>
            <CardFooter className="text-sm text-muted-foreground">
                Don't have an account?&nbsp;
                <Link
                    to={{
                        pathname: "/signup",
                        search: rawReturnUrl
                            ? `?returnUrl=${encodeURIComponent(rawReturnUrl)}`
                            : ""
                    }}
                    className="font-semibold text-primary hover:underline"
                >
                    Create a tenant
                </Link>
            </CardFooter>
        </Card>
    );
}
